import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
} from 'antd';
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

import type {
  CafeArticleAnalysisShopView,
  JsonTitleDescriptionItem,
  PartnerShopExtractedShopMap,
  PartnerShopReviewItem,
} from '../PartnerCreatePage';
import {
  getPartnerShopSelectedReviewIds,
  getReviewCandidates,
  type ReviewCandidateItem,
  type ReviewPointItem,
} from '@/api/partnerShops';

const { Text, Title, Paragraph } = Typography;

interface ReviewItemSelectStepProps {
  partnerShopId?: number;
  partnerShopExtractedShopMaps: PartnerShopExtractedShopMap[];

  partnerShopReviewItems: PartnerShopReviewItem[];
  onChangePartnerShopReviewItems: (
    nextPartnerShopReviewItems: PartnerShopReviewItem[]
  ) => void;

  cafeArticleAnalysisShopViews: Record<string, CafeArticleAnalysisShopView>;
  onChangeCafeArticleAnalysisShopViews: (
    nextCafeArticleAnalysisShopViews: Record<string, CafeArticleAnalysisShopView>
  ) => void;

  onGeneratePartnerShopReviewSummary: () => void;
  generatingPartnerShopReviewSummary: boolean;
}

interface ReviewGroup {
  key: string;
  title: string;
  items: PartnerShopReviewItem[];
}

interface BoardGroup {
  key: string;
  name: string;
  totalCount: number;
  publishedCount: number;
}

const EVALUATION_FILTER_OPTIONS = [
  { label: '전체', value: 'ALL' },
  { label: '직접 경험', value: '직접평가' },
  { label: '간접 평가', value: '간접평가' },
  { label: '사진 근거', value: '사진근거' },
  { label: '언급만', value: '언급만' },
];

function toPositiveNumber(value: unknown) {
  const numberValue = Number(value);

  if (Number.isNaN(numberValue) || numberValue < 1) {
    return null;
  }

  return numberValue;
}

function normalizePointItems(items?: ReviewPointItem[]): JsonTitleDescriptionItem[] {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => ({
      title: item.title || '',
      description: item.description || '',
    }))
    .filter((item) => item.title || item.description);
}

function getReviewItemKey(partnerShopReviewItem: PartnerShopReviewItem) {
  return String(partnerShopReviewItem.cafeArticleAnalysisShopId);
}

function getViewKey(cafeArticleAnalysisShopId: number | null | undefined) {
  if (!cafeArticleAnalysisShopId) return '';
  return String(cafeArticleAnalysisShopId);
}

function getConfidencePercent(confidence?: number | null) {
  if (confidence === undefined || confidence === null) return null;
  return Math.round(Number(confidence) * 100);
}

function getSentimentColor(sentiment?: string | null) {
  if (!sentiment) return 'default';
  if (sentiment.includes('긍정')) return 'blue';
  if (sentiment.includes('부정')) return 'red';
  if (sentiment.includes('혼합')) return 'purple';
  return 'default';
}

function getEvaluationGroupKey(view?: CafeArticleAnalysisShopView) {
  if (view?.evaluationLevel === '직접평가') return 'direct';
  if (view?.evaluationLevel === '간접평가') return 'indirect';
  if (view?.evaluationLevel === '사진근거') return 'photo';
  if (view?.evaluationLevel === '언급만') return 'mention';
  return 'etc';
}

function getEvaluationGroupTitle(key: string) {
  if (key === 'direct') return '직접 경험 후기';
  if (key === 'indirect') return '간접 평가 후기';
  if (key === 'photo') return '사진 근거 후기';
  if (key === 'mention') return '언급만 있는 후기';
  return '기타 후기';
}

function getBoardKey(view?: CafeArticleAnalysisShopView) {
  return view?.menuName || '게시판 없음';
}

function toPartnerShopReviewItem(
  item: ReviewCandidateItem,
  index: number,
  previousPartnerShopReviewItem?: PartnerShopReviewItem,
  selectedReviewIds?: Set<number>
): PartnerShopReviewItem | null {
  const cafeArticleAnalysisShopId = toPositiveNumber(item.sourceReviewId);

  if (!cafeArticleAnalysisShopId) {
    return null;
  }

  if (previousPartnerShopReviewItem) {
    return {
      ...previousPartnerShopReviewItem,
      cafeArticleAnalysisShopId,
      // 수정 모달에서 다시 불러올 때는 DB 기준 cafeArticleAnalysisShopId 선택 상태를 우선한다.
      isPublished: selectedReviewIds
        ? selectedReviewIds.has(cafeArticleAnalysisShopId)
          ? 1
          : 0
        : previousPartnerShopReviewItem.isPublished,
    };
  }

  return {
    cafeArticleAnalysisShopId,
    isPublished: selectedReviewIds
      ? selectedReviewIds.has(cafeArticleAnalysisShopId)
        ? 1
        : 0
      : 1,
    sortOrder: index + 1,
    delYn: 0,
  };
}

function toCafeArticleAnalysisShopView(
  item: ReviewCandidateItem
): CafeArticleAnalysisShopView | null {
  const cafeArticleAnalysisShopId = toPositiveNumber(item.sourceReviewId);
  const cafeArticleId = toPositiveNumber(item.sourceArticleId);

  if (!cafeArticleAnalysisShopId || !cafeArticleId) {
    return null;
  }

  return {
    cafeArticleAnalysisShopId,
    cafeArticleId,
    cafeArticleAnalysisId: toPositiveNumber(item.sourceAnalysisId),
    cafeExtractedShopId: toPositiveNumber(item.sourceExtractedShopId),

    title: item.title || '제목 없음',
    reviewDate: item.reviewDate || null,
    menuName: item.menuName || null,
    originalUrl: item.originalUrl || null,

    evaluationLevel: item.evaluationLevel || null,
    sentiment: item.sentiment || null,
    confidence:
      item.confidence === undefined || item.confidence === null
        ? null
        : Number(item.confidence),

    summary: item.summary || '',
    contentPreview: item.contentPreview || '',
    goodPointsJson: normalizePointItems(item.goodPoints),
    badPointsJson: normalizePointItems(item.badPoints),
    tagsJson: Array.isArray(item.tags) ? item.tags : [],
    thumbnailImagesJson: Array.isArray(item.thumbnailImageUrls)
      ? item.thumbnailImageUrls
      : [],
  };
}

export default function ReviewItemSelectStep({
  partnerShopId,
  partnerShopExtractedShopMaps,
  partnerShopReviewItems,
  onChangePartnerShopReviewItems,
  cafeArticleAnalysisShopViews,
  onChangeCafeArticleAnalysisShopViews,
  onGeneratePartnerShopReviewSummary,
  generatingPartnerShopReviewSummary,
}: ReviewItemSelectStepProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [activeBoardKey, setActiveBoardKey] = useState<string>('ALL');
  const [activeEvaluationLevel, setActiveEvaluationLevel] =
    useState<string>('ALL');
  const [keyword, setKeyword] = useState<string>('');
  const [limit, setLimit] = useState<number>(200);
  const [confidenceLimit, setConfidenceLimit] = useState<number>(70);
  const [includeUnknownConfidence, setIncludeUnknownConfidence] =
    useState<boolean>(true);

  const sourceCafeExtractedShopIds = useMemo(() => {
    return partnerShopExtractedShopMaps
      .map((item) => Number(item.cafeExtractedShopId))
      .filter((id) => !Number.isNaN(id) && id > 0);
  }, [partnerShopExtractedShopMaps]);

  const visiblePartnerShopReviewItems = useMemo(() => {
    return Array.isArray(partnerShopReviewItems)
      ? partnerShopReviewItems.filter((item) => Number(item.delYn || 0) === 0)
      : [];
  }, [partnerShopReviewItems]);

  const publishedCount = visiblePartnerShopReviewItems.filter(
    (item) => Number(item.isPublished) === 1
  ).length;

  const hiddenCount = visiblePartnerShopReviewItems.length - publishedCount;

  const boardGroups = useMemo<BoardGroup[]>(() => {
    const boardMap = new Map<string, BoardGroup>();

    visiblePartnerShopReviewItems.forEach((partnerShopReviewItem) => {
      const view =
        cafeArticleAnalysisShopViews[
          getViewKey(partnerShopReviewItem.cafeArticleAnalysisShopId)
        ];

      const key = getBoardKey(view);

      if (!boardMap.has(key)) {
        boardMap.set(key, {
          key,
          name: key,
          totalCount: 0,
          publishedCount: 0,
        });
      }

      const boardGroup = boardMap.get(key);

      if (!boardGroup) return;

      boardGroup.totalCount += 1;

      if (Number(partnerShopReviewItem.isPublished) === 1) {
        boardGroup.publishedCount += 1;
      }
    });

    return Array.from(boardMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name, 'ko')
    );
  }, [visiblePartnerShopReviewItems, cafeArticleAnalysisShopViews]);

  const filteredPartnerShopReviewItems = useMemo(() => {
    const trimmedKeyword = keyword.trim();

    return visiblePartnerShopReviewItems.filter((partnerShopReviewItem) => {
      const view =
        cafeArticleAnalysisShopViews[
          getViewKey(partnerShopReviewItem.cafeArticleAnalysisShopId)
        ];

      if (activeBoardKey !== 'ALL' && getBoardKey(view) !== activeBoardKey) {
        return false;
      }

      if (
        activeEvaluationLevel !== 'ALL' &&
        view?.evaluationLevel !== activeEvaluationLevel
      ) {
        return false;
      }

      if (!trimmedKeyword) {
        return true;
      }

      return (
        view?.title?.includes(trimmedKeyword) ||
        view?.summary?.includes(trimmedKeyword) ||
        view?.contentPreview?.includes(trimmedKeyword) ||
        view?.tagsJson?.some((tag) => tag.includes(trimmedKeyword))
      );
    });
  }, [
    visiblePartnerShopReviewItems,
    cafeArticleAnalysisShopViews,
    activeBoardKey,
    activeEvaluationLevel,
    keyword,
  ]);

  const groupedPartnerShopReviewItems = useMemo<ReviewGroup[]>(() => {
    const groupMap = new Map<string, ReviewGroup>();

    filteredPartnerShopReviewItems.forEach((partnerShopReviewItem) => {
      const view =
        cafeArticleAnalysisShopViews[
          getViewKey(partnerShopReviewItem.cafeArticleAnalysisShopId)
        ];

      const key = getEvaluationGroupKey(view);

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          key,
          title: getEvaluationGroupTitle(key),
          items: [],
        });
      }

      groupMap.get(key)?.items.push(partnerShopReviewItem);
    });

    const orderMap: Record<string, number> = {
      direct: 1,
      indirect: 2,
      photo: 3,
      mention: 4,
      etc: 9,
    };

    return Array.from(groupMap.values())
      .map((group) => ({
        ...group,
        items: [...group.items].sort(
          (a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0)
        ),
      }))
      .sort((a, b) => (orderMap[a.key] || 99) - (orderMap[b.key] || 99));
  }, [filteredPartnerShopReviewItems, cafeArticleAnalysisShopViews]);

  const loadReviewCandidates = async () => {
    if (sourceCafeExtractedShopIds.length === 0) {
      message.warning('먼저 연결할 업체를 선택해 주세요.');
      return;
    }

    setLoading(true);

    try {
      const [result, selectedIdsResult] = await Promise.all([
        getReviewCandidates({
          cafeExtractedShopIds: sourceCafeExtractedShopIds,
          limit,
        }),
        partnerShopId
          ? getPartnerShopSelectedReviewIds(partnerShopId)
          : Promise.resolve(null),
      ]);

      const selectedReviewIds = selectedIdsResult
        ? new Set((selectedIdsResult.reviewIds || []).map((id) => Number(id)))
        : undefined;

      const previousByCafeArticleAnalysisShopId = new Map<
        number,
        PartnerShopReviewItem
      >();

      visiblePartnerShopReviewItems.forEach((partnerShopReviewItem) => {
        const cafeArticleAnalysisShopId = toPositiveNumber(
          partnerShopReviewItem.cafeArticleAnalysisShopId
        );

        if (cafeArticleAnalysisShopId) {
          previousByCafeArticleAnalysisShopId.set(
            cafeArticleAnalysisShopId,
            partnerShopReviewItem
          );
        }
      });

      const nextCafeArticleAnalysisShopViews = {
        ...cafeArticleAnalysisShopViews,
      };

      const nextPartnerShopReviewItems = result.list
        .map((item: ReviewCandidateItem, index: number) => {
          const cafeArticleAnalysisShopId = toPositiveNumber(item.sourceReviewId);

          const previousPartnerShopReviewItem = cafeArticleAnalysisShopId
            ? previousByCafeArticleAnalysisShopId.get(cafeArticleAnalysisShopId)
            : undefined;

          const nextPartnerShopReviewItem = toPartnerShopReviewItem(
            item,
            index,
            previousPartnerShopReviewItem,
            selectedReviewIds
          );

          const cafeArticleAnalysisShopView = toCafeArticleAnalysisShopView(item);

          if (cafeArticleAnalysisShopView) {
            nextCafeArticleAnalysisShopViews[
              String(cafeArticleAnalysisShopView.cafeArticleAnalysisShopId)
            ] = cafeArticleAnalysisShopView;
          }

          return nextPartnerShopReviewItem;
        })
        .filter(Boolean) as PartnerShopReviewItem[];

      onChangeCafeArticleAnalysisShopViews(nextCafeArticleAnalysisShopViews);
      onChangePartnerShopReviewItems(nextPartnerShopReviewItems);

      setActiveBoardKey('ALL');
      setActiveEvaluationLevel('ALL');

      message.success(
        `후기 ${nextPartnerShopReviewItems.length.toLocaleString()}개를 불러왔습니다.`
      );
    } catch (error) {
      console.error(
        '[ReviewItemSelectStep] failed to load review candidates',
        error
      );

      const errorMessage =
        error instanceof Error ? error.message : '후기를 불러오지 못했습니다.';

      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updatePartnerShopReviewItem = (
    partnerShopReviewItemKey: string,
    patch: Partial<PartnerShopReviewItem>
  ) => {
    onChangePartnerShopReviewItems(
      visiblePartnerShopReviewItems.map((partnerShopReviewItem) => {
        if (getReviewItemKey(partnerShopReviewItem) !== partnerShopReviewItemKey) {
          return partnerShopReviewItem;
        }

        return {
          ...partnerShopReviewItem,
          ...patch,
        };
      })
    );
  };

  const updateAllPublished = (isPublished: number) => {
    onChangePartnerShopReviewItems(
      visiblePartnerShopReviewItems.map((partnerShopReviewItem) => ({
        ...partnerShopReviewItem,
        isPublished,
      }))
    );
  };

  const updateBoardPublished = (boardKey: string, isPublished: number) => {
    onChangePartnerShopReviewItems(
      visiblePartnerShopReviewItems.map((partnerShopReviewItem) => {
        const view =
          cafeArticleAnalysisShopViews[
            getViewKey(partnerShopReviewItem.cafeArticleAnalysisShopId)
          ];

        if (getBoardKey(view) !== boardKey) {
          return partnerShopReviewItem;
        }

        return {
          ...partnerShopReviewItem,
          isPublished,
        };
      })
    );
  };

  const updateGroupPublished = (groupKey: string, isPublished: number) => {
    const targetGroup = groupedPartnerShopReviewItems.find(
      (group) => group.key === groupKey
    );

    if (!targetGroup) return;

    const targetKeySet = new Set(targetGroup.items.map(getReviewItemKey));

    onChangePartnerShopReviewItems(
      visiblePartnerShopReviewItems.map((partnerShopReviewItem) => {
        if (!targetKeySet.has(getReviewItemKey(partnerShopReviewItem))) {
          return partnerShopReviewItem;
        }

        return {
          ...partnerShopReviewItem,
          isPublished,
        };
      })
    );
  };

  const updateGroupSortOrder = (groupKey: string, startSortOrder: number) => {
    const targetGroup = groupedPartnerShopReviewItems.find(
      (group) => group.key === groupKey
    );

    if (!targetGroup) return;

    const targetKeySet = new Set(targetGroup.items.map(getReviewItemKey));
    let currentSortOrder = startSortOrder;

    onChangePartnerShopReviewItems(
      visiblePartnerShopReviewItems.map((partnerShopReviewItem) => {
        if (!targetKeySet.has(getReviewItemKey(partnerShopReviewItem))) {
          return partnerShopReviewItem;
        }

        const nextItem = {
          ...partnerShopReviewItem,
          sortOrder: currentSortOrder,
        };

        currentSortOrder += 1;

        return nextItem;
      })
    );
  };

  const hideLowConfidenceReviews = () => {
    const limitValue = Number(confidenceLimit || 0);
    let changedCount = 0;

    const nextPartnerShopReviewItems = visiblePartnerShopReviewItems.map(
      (partnerShopReviewItem) => {
        const view =
          cafeArticleAnalysisShopViews[
            getViewKey(partnerShopReviewItem.cafeArticleAnalysisShopId)
          ];

        const confidencePercent = getConfidencePercent(view?.confidence);

        const shouldHide =
          confidencePercent === null
            ? includeUnknownConfidence
            : confidencePercent <= limitValue;

        if (!shouldHide || Number(partnerShopReviewItem.isPublished) === 0) {
          return partnerShopReviewItem;
        }

        changedCount += 1;

        return {
          ...partnerShopReviewItem,
          isPublished: 0,
        };
      }
    );

    onChangePartnerShopReviewItems(nextPartnerShopReviewItems);

    message.success(
      `조건에 맞는 후기 ${changedCount.toLocaleString()}개를 숨김 처리했습니다.`
    );
  };

  const renderPointList = (title: string, items?: JsonTitleDescriptionItem[]) => {
    if (!items || items.length === 0) return null;

    return (
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Text strong>{title}</Text>

        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          {items.slice(0, 3).map((item, index) => (
            <Text key={`${item.title}-${index}`} style={{ fontSize: 12 }}>
              · {item.title}
              {item.description ? ` — ${item.description}` : ''}
            </Text>
          ))}
        </Space>
      </Space>
    );
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              후기 선택
            </Title>
            <Text type="secondary">
              연결된 업체 기준으로 후보 후기를 불러오고, 고객에게 보여줄 후기를 정리합니다.
              게시판과 후기 성격별로 확인할 수 있습니다.
            </Text>
          </div>

          {sourceCafeExtractedShopIds.length === 0 && (
            <Alert
              type="warning"
              showIcon
              message="연결된 업체가 없습니다."
              description="먼저 첫 단계에서 업체를 선택해야 후기를 불러올 수 있습니다."
            />
          )}

          <Row gutter={[16, 8]}>
            <Col>
              <Text strong>
                전체 {visiblePartnerShopReviewItems.length.toLocaleString()}개
              </Text>
            </Col>
            <Col>
              <Text strong type="success">
                노출 {publishedCount.toLocaleString()}개
              </Text>
            </Col>
            <Col>
              <Text type="secondary">
                숨김 {hiddenCount.toLocaleString()}개
              </Text>
            </Col>
          </Row>

          <Space wrap>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={loading}
              disabled={sourceCafeExtractedShopIds.length === 0}
              onClick={loadReviewCandidates}
            >
              후기 불러오기
            </Button>

            <Button
              type="primary"
              loading={generatingPartnerShopReviewSummary}
              disabled={sourceCafeExtractedShopIds.length === 0}
              onClick={onGeneratePartnerShopReviewSummary}
            >
              AI 후기 요약 생성
            </Button>

            <Button
              icon={<EyeOutlined />}
              disabled={visiblePartnerShopReviewItems.length === 0}
              onClick={() => updateAllPublished(1)}
            >
              전체 노출
            </Button>

            <Button
              icon={<EyeInvisibleOutlined />}
              disabled={visiblePartnerShopReviewItems.length === 0}
              onClick={() => updateAllPublished(0)}
            >
              전체 숨김
            </Button>

            <InputNumber
              min={1}
              max={500}
              value={limit}
              addonBefore="불러올 수"
              addonAfter="개"
              onChange={(nextValue) => setLimit(Number(nextValue || 200))}
            />

            <Input.Search
              allowClear
              placeholder="제목, 요약, 키워드 검색"
              style={{ width: 260 }}
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
            />
          </Space>

          <Space wrap>
            <Select
              value={activeBoardKey}
              style={{ width: 220 }}
              onChange={setActiveBoardKey}
              options={[
                {
                  label: `전체 게시판 (${visiblePartnerShopReviewItems.length})`,
                  value: 'ALL',
                },
                ...boardGroups.map((boardGroup) => ({
                  label: `${boardGroup.name} (${boardGroup.publishedCount}/${boardGroup.totalCount})`,
                  value: boardGroup.key,
                })),
              ]}
            />

            <Select
              value={activeEvaluationLevel}
              style={{ width: 160 }}
              onChange={setActiveEvaluationLevel}
              options={EVALUATION_FILTER_OPTIONS}
            />

            <InputNumber
              min={0}
              max={100}
              value={confidenceLimit}
              addonBefore="신뢰도"
              addonAfter="% 이하"
              onChange={(nextValue) =>
                setConfidenceLimit(Number(nextValue || 0))
              }
            />

            <Space>
              <Text type="secondary">신뢰도 없음 포함</Text>
              <Switch
                checked={includeUnknownConfidence}
                onChange={setIncludeUnknownConfidence}
              />
            </Space>

            <Button
              danger
              disabled={visiblePartnerShopReviewItems.length === 0}
              onClick={hideLowConfidenceReviews}
            >
              조건 이하 숨김
            </Button>
          </Space>
        </Space>
      </Card>

      {boardGroups.length > 0 && (
        <Card size="small">
          <Space wrap>
            {boardGroups.map((boardGroup) => {
              const allPublished =
                boardGroup.totalCount > 0 &&
                boardGroup.publishedCount === boardGroup.totalCount;

              return (
                <Space
                  key={boardGroup.key}
                  style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: 999,
                    padding: '4px 10px',
                  }}
                >
                  <Text>{boardGroup.name}</Text>
                  <Tag>{boardGroup.totalCount.toLocaleString()}개</Tag>
                  <Switch
                    size="small"
                    checked={allPublished}
                    onChange={(checked) =>
                      updateBoardPublished(boardGroup.key, checked ? 1 : 0)
                    }
                  />
                </Space>
              );
            })}
          </Space>
        </Card>
      )}

      {loading && (
        <Card>
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">후기를 불러오는 중입니다.</Text>
            </div>
          </div>
        </Card>
      )}

      {!loading && visiblePartnerShopReviewItems.length === 0 && (
        <Card>
          <Empty description="아직 불러온 후기가 없습니다.">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              disabled={sourceCafeExtractedShopIds.length === 0}
              onClick={loadReviewCandidates}
            >
              후기 불러오기
            </Button>
          </Empty>
        </Card>
      )}

      {!loading &&
        visiblePartnerShopReviewItems.length > 0 &&
        groupedPartnerShopReviewItems.length === 0 && (
          <Card>
            <Empty description="검색 조건에 맞는 후기가 없습니다." />
          </Card>
        )}

      {!loading &&
        groupedPartnerShopReviewItems.map((group) => {
          const groupPublishedCount = group.items.filter(
            (item) => Number(item.isPublished) === 1
          ).length;

          const allGroupPublished =
            group.items.length > 0 && groupPublishedCount === group.items.length;

          return (
            <Card
              key={group.key}
              title={
                <Space>
                  <Text strong>{group.title}</Text>
                  <Tag>{group.items.length.toLocaleString()}개</Tag>
                  <Tag color={groupPublishedCount > 0 ? 'blue' : 'default'}>
                    노출 {groupPublishedCount.toLocaleString()}개
                  </Tag>
                </Space>
              }
              extra={
                <Space wrap>
                  <Text type="secondary">그룹 전체 노출</Text>
                  <Switch
                    checked={allGroupPublished}
                    onChange={(checked) =>
                      updateGroupPublished(group.key, checked ? 1 : 0)
                    }
                  />

                  <InputNumber
                    min={0}
                    placeholder="정렬 시작"
                    style={{ width: 110 }}
                    onPressEnter={(event) => {
                      const target = event.target as HTMLInputElement;
                      updateGroupSortOrder(
                        group.key,
                        Number(target.value || 0)
                      );
                    }}
                  />
                </Space>
              }
            >
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {group.items.map((partnerShopReviewItem) => {
                  const partnerShopReviewItemKey =
                    getReviewItemKey(partnerShopReviewItem);

                  const view =
                    cafeArticleAnalysisShopViews[
                      getViewKey(partnerShopReviewItem.cafeArticleAnalysisShopId)
                    ];

                  const confidencePercent = getConfidencePercent(view?.confidence);

                  return (
                    <Card
                      key={partnerShopReviewItemKey}
                      size="small"
                      bodyStyle={{
                        opacity:
                          Number(partnerShopReviewItem.isPublished) === 1
                            ? 1
                            : 0.45,
                      }}
                    >
                      <Row gutter={[12, 12]}>
                        <Col xs={24} md={5}>
                          <Space
                            direction="vertical"
                            size={8}
                            style={{ width: '100%' }}
                          >
                            <Switch
                              checked={
                                Number(partnerShopReviewItem.isPublished) === 1
                              }
                              checkedChildren="노출"
                              unCheckedChildren="숨김"
                              onChange={(checked) =>
                                updatePartnerShopReviewItem(
                                  partnerShopReviewItemKey,
                                  {
                                    isPublished: checked ? 1 : 0,
                                  }
                                )
                              }
                            />

                            <InputNumber
                              min={0}
                              value={partnerShopReviewItem.sortOrder}
                              addonBefore="정렬"
                              style={{ width: '100%' }}
                              onChange={(nextValue) =>
                                updatePartnerShopReviewItem(
                                  partnerShopReviewItemKey,
                                  {
                                    sortOrder: Number(nextValue || 0),
                                  }
                                )
                              }
                            />

                            <Space wrap>
                              {view?.evaluationLevel && (
                                <Tag>{view.evaluationLevel}</Tag>
                              )}

                              {view?.sentiment && (
                                <Tag color={getSentimentColor(view.sentiment)}>
                                  {view.sentiment}
                                </Tag>
                              )}

                              {confidencePercent !== null && (
                                <Tag>{confidencePercent}%</Tag>
                              )}
                            </Space>

                            {view?.thumbnailImagesJson?.length > 0 && (
                              <Row gutter={[4, 4]}>
                                {view.thumbnailImagesJson
                                  .slice(0, 4)
                                  .map((imageUrl, index) => (
                                    <Col span={12} key={`${imageUrl}-${index}`}>
                                      <img
                                        src={imageUrl}
                                        alt="후기 이미지"
                                        loading="lazy"
                                        style={{
                                          width: '100%',
                                          aspectRatio: '1 / 1',
                                          objectFit: 'cover',
                                          borderRadius: 6,
                                          background: '#f5f5f5',
                                        }}
                                      />
                                    </Col>
                                  ))}
                              </Row>
                            )}
                          </Space>
                        </Col>

                        <Col xs={24} md={19}>
                          <Space
                            direction="vertical"
                            size={10}
                            style={{ width: '100%' }}
                          >
                            <div>
                              <Space wrap size={6}>
                                <Text strong>
                                  {view?.title || '제목 없음'}
                                </Text>

                                {view?.menuName && (
                                  <Tag>{view.menuName}</Tag>
                                )}

                                {view?.reviewDate && (
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {String(view.reviewDate).slice(0, 10)}
                                  </Text>
                                )}
                              </Space>
                            </div>

                            {view?.summary && (
                              <Paragraph style={{ marginBottom: 0 }}>
                                {view.summary}
                              </Paragraph>
                            )}

                            {!view?.summary && view?.contentPreview && (
                              <Paragraph
                                type="secondary"
                                ellipsis={{ rows: 2 }}
                                style={{ marginBottom: 0 }}
                              >
                                {view.contentPreview}
                              </Paragraph>
                            )}

                            <Row gutter={[12, 8]}>
                              <Col xs={24} md={12}>
                                {renderPointList(
                                  '좋았던 점',
                                  view?.goodPointsJson
                                )}
                              </Col>

                              <Col xs={24} md={12}>
                                {renderPointList(
                                  '확인할 점',
                                  view?.badPointsJson
                                )}
                              </Col>
                            </Row>

                            {view?.tagsJson?.length > 0 && (
                              <Space wrap>
                                {view.tagsJson.slice(0, 12).map((tag) => (
                                  <Tag key={tag}>{tag}</Tag>
                                ))}
                              </Space>
                            )}
                          </Space>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
              </Space>
            </Card>
          );
        })}
    </Space>
  );
}
